import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { TextsProvider } from "./contexts/TextsContext";
import { TrackingProvider } from "./contexts/TrackingProvider";
import StickyMobileCTA from "./components/StickyMobileCTA";
import RedirectGate from "./components/RedirectGate";
import RedirectsAdmin from "./pages/admin/RedirectsAdmin";
import { ProtectedRoute } from "./components/ProtectedRoute";

import Landing from "./pages/Landing";
import HomePage from "./pages/HomePage";
import Listings from "./pages/Listings";
import PropertyDetail from "./pages/PropertyDetail";
import ReservationFlow from "./pages/ReservationFlow";
import CustomRequest from "./pages/CustomRequest";
import Contact from "./pages/Contact";
import About from "./pages/About";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import DynamicPage from "./pages/DynamicPage";
import PaymentSuccess from "./pages/PaymentSuccess";

import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import PropertyForm from "./pages/admin/PropertyForm";
import BlogForm from "./pages/admin/BlogForm";
import SiteContentEditor from "./pages/admin/SiteContentEditor";
import PagesAdmin from "./pages/admin/PagesAdmin";
import PageForm from "./pages/admin/PageForm";
import MediaLibrary from "./pages/admin/MediaLibrary";
import UsersAdmin from "./pages/admin/UsersAdmin";
import TestimonialsAdmin from "./pages/admin/TestimonialsAdmin";
import PartnersAdmin from "./pages/admin/PartnersAdmin";
import SiteTextsAdmin from "./pages/admin/SiteTextsAdmin";
import ActivityTypesAdmin from "./pages/admin/ActivityTypesAdmin";
import ReservationDetail from "./pages/admin/ReservationDetail";
import TenantsAdmin from "./pages/admin/TenantsAdmin";
import LeasesAdmin from "./pages/admin/LeasesAdmin";
import RentsAdmin from "./pages/admin/RentsAdmin";
import IncidentsAdmin from "./pages/admin/IncidentsAdmin";
import MessagesAdmin from "./pages/admin/MessagesAdmin";
import InventoriesAdmin from "./pages/admin/InventoriesAdmin";
import IrlAdmin from "./pages/admin/IrlAdmin";
import TenantLogin from "./pages/tenant/TenantLogin";
import TenantDashboard from "./pages/tenant/TenantDashboard";
import TenantHub from "./pages/tenant/TenantHub";
import FormsAdmin from "./pages/admin/FormsAdmin";
import FormBuilder from "./pages/admin/FormBuilder";
import FormSubmissions from "./pages/admin/FormSubmissions";
import PublicForm from "./pages/PublicForm";
import TrackingAdmin from "./pages/admin/TrackingAdmin";

// Back-Office Foncière (overlay module — n'interfère pas avec le site public)
import BackofficeHome from "./pages/backoffice/BackofficeHome";
import EntitiesAdmin from "./pages/backoffice/EntitiesAdmin";
import BankAccountsAdmin from "./pages/backoffice/BankAccountsAdmin";
import BankTransactionsAdmin from "./pages/backoffice/BankTransactionsAdmin";
import ProjectsAdmin from "./pages/backoffice/ProjectsAdmin";
import ProjectDetail from "./pages/backoffice/ProjectDetail";
import LoansAdmin from "./pages/backoffice/LoansAdmin";
import RecurringAdmin from "./pages/backoffice/RecurringAdmin";
import RulesAdmin from "./pages/backoffice/RulesAdmin";
import Simulations from "./pages/backoffice/Simulations";
import BofLeasesAdmin from "./pages/backoffice/LeasesAdmin";
import TreasuryView from "./pages/backoffice/TreasuryView";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
        <TextsProvider>
        <AuthProvider>
        <TrackingProvider>
          <RedirectGate>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/locaux" element={<Listings />} />
            <Route path="/locaux/:id" element={<PropertyDetail />} />
            <Route path="/reserver/:id" element={<ReservationFlow />} />
            <Route path="/demande" element={<CustomRequest />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/p/:slug" element={<DynamicPage />} />
            <Route path="/f/:slug" element={<PublicForm />} />
            <Route path="/paiement/succes" element={<PaymentSuccess />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/biens/nouveau" element={<ProtectedRoute><PropertyForm /></ProtectedRoute>} />
            <Route path="/admin/biens/:id/edit" element={<ProtectedRoute><PropertyForm /></ProtectedRoute>} />
            <Route path="/admin/blog/nouveau" element={<ProtectedRoute><BlogForm /></ProtectedRoute>} />
            <Route path="/admin/blog/:id/edit" element={<ProtectedRoute><BlogForm /></ProtectedRoute>} />
            <Route path="/admin/contenu" element={<ProtectedRoute><SiteContentEditor /></ProtectedRoute>} />
            <Route path="/admin/textes" element={<ProtectedRoute><SiteTextsAdmin /></ProtectedRoute>} />
            <Route path="/admin/activites" element={<ProtectedRoute><ActivityTypesAdmin /></ProtectedRoute>} />
            <Route path="/admin/reservations/:id" element={<ProtectedRoute><ReservationDetail /></ProtectedRoute>} />
            <Route path="/admin/locataires" element={<ProtectedRoute><TenantsAdmin /></ProtectedRoute>} />
            <Route path="/admin/baux" element={<ProtectedRoute><LeasesAdmin /></ProtectedRoute>} />
            <Route path="/admin/loyers" element={<ProtectedRoute><RentsAdmin /></ProtectedRoute>} />
            <Route path="/admin/incidents" element={<ProtectedRoute><IncidentsAdmin /></ProtectedRoute>} />
            <Route path="/admin/messages" element={<ProtectedRoute><MessagesAdmin /></ProtectedRoute>} />
            <Route path="/admin/etats-des-lieux" element={<ProtectedRoute><InventoriesAdmin /></ProtectedRoute>} />
            <Route path="/admin/irl" element={<ProtectedRoute><IrlAdmin /></ProtectedRoute>} />
            <Route path="/admin/pages" element={<ProtectedRoute><PagesAdmin /></ProtectedRoute>} />
            <Route path="/admin/pages/nouvelle" element={<ProtectedRoute><PageForm /></ProtectedRoute>} />
            <Route path="/admin/pages/:id/edit" element={<ProtectedRoute><PageForm /></ProtectedRoute>} />
            <Route path="/admin/medias" element={<ProtectedRoute><MediaLibrary /></ProtectedRoute>} />
            <Route path="/admin/utilisateurs" element={<ProtectedRoute><UsersAdmin /></ProtectedRoute>} />
            <Route path="/admin/temoignages" element={<ProtectedRoute><TestimonialsAdmin /></ProtectedRoute>} />
            <Route path="/admin/partenaires" element={<ProtectedRoute><PartnersAdmin /></ProtectedRoute>} />
            <Route path="/admin/formulaires" element={<ProtectedRoute><FormsAdmin /></ProtectedRoute>} />
            <Route path="/admin/formulaires/nouveau" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
            <Route path="/admin/formulaires/:id/edit" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
            <Route path="/admin/formulaires/:id/soumissions" element={<ProtectedRoute><FormSubmissions /></ProtectedRoute>} />
            <Route path="/admin/tracking" element={<ProtectedRoute><TrackingAdmin /></ProtectedRoute>} />
            <Route path="/admin/redirections" element={<ProtectedRoute><RedirectsAdmin /></ProtectedRoute>} />

            {/* Back-Office Foncière (overlay module) */}
            <Route path="/backoffice-finance" element={<ProtectedRoute><BackofficeHome /></ProtectedRoute>} />
            <Route path="/backoffice-finance/tresorerie" element={<ProtectedRoute><TreasuryView /></ProtectedRoute>} />
            <Route path="/backoffice-finance/entites" element={<ProtectedRoute><EntitiesAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/comptes" element={<ProtectedRoute><BankAccountsAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/transactions" element={<ProtectedRoute><BankTransactionsAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/baux" element={<ProtectedRoute><BofLeasesAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/projets" element={<ProtectedRoute><ProjectsAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/projets/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/backoffice-finance/prets" element={<ProtectedRoute><LoansAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/recurrents" element={<ProtectedRoute><RecurringAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/regles" element={<ProtectedRoute><RulesAdmin /></ProtectedRoute>} />
            <Route path="/backoffice-finance/simulations" element={<ProtectedRoute><Simulations /></ProtectedRoute>} />

            <Route path="/locataire/login" element={<TenantLogin />} />
            <Route path="/locataire" element={<ProtectedRoute role="tenant"><TenantDashboard /></ProtectedRoute>} />
            <Route path="/locataire/suivi" element={<ProtectedRoute role="tenant"><TenantHub /></ProtectedRoute>} />

            {/* Catch-all clean URL: /<slug> serves dynamic CMS pages too.
                MUST be last route to avoid shadowing the explicit ones above. */}
            <Route path="/:slug" element={<DynamicPage />} />
          </Routes>
          </RedirectGate>
          <StickyMobileCTA />
        </TrackingProvider>
        </AuthProvider>
        </TextsProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
